import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import type { CrearUsuario, Rol, Ubicacion, UsuarioResumen } from '@manejo-alcanza/contracts';
import {
  cambiarEstadoUsuario,
  crearUsuario,
  listarUsuarios,
  reiniciarClaveDe,
} from '../api';
import { Aviso, Boton, Card, Cargando, Error_, Pill, Tabla } from '../componentes/basicos';

/**
 * Quién entra al sistema. **Solo el administrador.**
 *
 * Antes no existía: dar de alta a alguien obligaba a escribir en la base a
 * mano, y ninguna alta quedaba auditada. Aquí todo queda registrado con quién
 * lo hizo.
 *
 * El botón dice «Desactivar», no «Eliminar»: nada con histórico se borra
 * (invariante 5).
 */

const ROLES: Rol[] = [
  'ADMINISTRADOR',
  'COMERCIAL_FINANCIERO',
  'CONTADOR',
  'PRODUCTOR',
  'REPARTIDOR',
  'DESARROLLADOR',
];

const OPERATIVOS: Rol[] = ['PRODUCTOR', 'REPARTIDOR'];

export function Usuarios(): ReactNode {
  const cliente = useQueryClient();
  const [abierto, setAbierto] = useState(false);

  const usuarios = useQuery({ queryKey: ['usuarios'], queryFn: listarUsuarios });
  const refrescar = (): void => void cliente.invalidateQueries({ queryKey: ['usuarios'] });

  if (usuarios.isLoading) return <Cargando que="los usuarios" />;
  if (usuarios.isError) return <Error_>No se pudieron cargar los usuarios</Error_>;

  const filas = usuarios.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Usuarios</h1>
        <Boton onClick={() => setAbierto((v) => !v)}>
          {abierto ? 'Cancelar' : 'Crear usuario'}
        </Boton>
      </div>

      {abierto && (
        <Formulario
          alCrear={() => {
            setAbierto(false);
            refrescar();
          }}
        />
      )}

      <Tabla>
        <thead>
          <tr className="border-b border-borde text-left text-xs uppercase tracking-wide text-tenue">
            <th className="px-3 py-2 font-medium">Nombre</th>
            <th className="px-3 py-2 font-medium">Correo</th>
            <th className="px-3 py-2 font-medium">Teléfono</th>
            <th className="px-3 py-2 font-medium">Rol</th>
            <th className="px-3 py-2 font-medium">Ubicación</th>
            <th className="px-3 py-2 font-medium">Estado</th>
            <th className="px-3 py-2 font-medium">Último acceso</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {filas.map((u) => (
            <Fila key={u.id} usuario={u} alCambiar={refrescar} />
          ))}
        </tbody>
      </Tabla>
    </div>
  );
}

function Fila({
  usuario,
  alCambiar,
}: {
  usuario: UsuarioResumen;
  alCambiar: () => void;
}): ReactNode {
  const [error, setError] = useState<string | null>(null);

  const estado = useMutation({
    mutationFn: (activo: boolean) =>
      cambiarEstadoUsuario(usuario.id, {
        activo,
        motivo: activo ? 'Reactivado por el administrador' : 'Desactivado por el administrador',
      }),
    onSuccess: alCambiar,
    onError: (e: Error) => setError(e.message),
  });

  const clave = useMutation({
    mutationFn: (claveNueva: string) =>
      reiniciarClaveDe(usuario.id, { claveNueva, motivo: 'Contraseña olvidada' }),
    onSuccess: alCambiar,
    onError: (e: Error) => setError(e.message),
  });

  return (
    <tr className="border-b border-borde last:border-0">
      <td className="px-3 py-2">{usuario.nombre}</td>
      <td className="px-3 py-2 font-mono text-xs">{usuario.email}</td>
      <td className="px-3 py-2 font-mono text-xs">
        {usuario.telefono ?? <span className="text-tenue">—</span>}
      </td>
      <td className="px-3 py-2 text-sm">{usuario.rol.replaceAll('_', ' ').toLowerCase()}</td>
      <td className="px-3 py-2 text-sm text-tenue">
        {usuario.ubicacionAsignada?.replaceAll('_', ' ').toLowerCase() ?? '—'}
      </td>
      <td className="px-3 py-2">
        <Pill estado={usuario.activo ? 'RECIBIDA' : 'ANULADA'} />
        {usuario.bloqueado && (
          <span className="ml-2 text-xs text-aviso" title="Demasiados intentos fallidos seguidos">
            bloqueado
          </span>
        )}
        {usuario.debeCambiarClave && (
          <span className="ml-2 text-xs text-tenue">clave sin estrenar</span>
        )}
      </td>
      <td className="px-3 py-2 text-xs text-tenue">
        {usuario.ultimoAcceso === null
          ? 'nunca'
          : new Date(usuario.ultimoAcceso).toLocaleDateString('es-CO')}
      </td>
      <td className="px-3 py-2">
        <div className="flex justify-end gap-2">
          <ReiniciarClave
            pendiente={clave.isPending}
            alConfirmar={(nueva) => clave.mutate(nueva)}
          />
          <Boton
            tipo={usuario.activo ? 'peligro' : 'secundario'}
            deshabilitado={estado.isPending}
            onClick={() => estado.mutate(!usuario.activo)}
          >
            {usuario.activo ? 'Desactivar' : 'Reactivar'}
          </Boton>
        </div>
        {error !== null && <p className="mt-1 text-right text-xs text-alerta">{error}</p>}
      </td>
    </tr>
  );
}

/**
 * La clave temporal la escribe el administrador y se la entrega en persona.
 *
 * No se genera sola ni se muestra en pantalla después: una clave que queda
 * escrita en una interfaz acaba en una captura de pantalla.
 */
function ReiniciarClave({
  pendiente,
  alConfirmar,
}: {
  pendiente: boolean;
  alConfirmar: (clave: string) => void;
}): ReactNode {
  const [abierto, setAbierto] = useState(false);
  const [clave, setClave] = useState('');

  if (!abierto) {
    return (
      <Boton tipo="secundario" onClick={() => setAbierto(true)}>
        Reiniciar clave
      </Boton>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <input
        type="text"
        value={clave}
        onChange={(e) => setClave(e.target.value)}
        placeholder="clave temporal (12+)"
        className="w-44 rounded-md border border-borde bg-superficie px-2 py-1 text-xs"
      />
      <Boton
        deshabilitado={pendiente || clave.length < 12}
        onClick={() => {
          alConfirmar(clave);
          setClave('');
          setAbierto(false);
        }}
      >
        Aplicar
      </Boton>
      <Boton tipo="secundario" onClick={() => setAbierto(false)}>
        No
      </Boton>
    </div>
  );
}

function Formulario({ alCrear }: { alCrear: () => void }): ReactNode {
  const [email, setEmail] = useState('');
  const [nombre, setNombre] = useState('');
  const [telefono, setTelefono] = useState('');
  const [rol, setRol] = useState<Rol>('COMERCIAL_FINANCIERO');
  const [ubicacion, setUbicacion] = useState<Ubicacion>('PLANTA');
  const [claveInicial, setClaveInicial] = useState('');
  const [error, setError] = useState<string | null>(null);

  const operativo = OPERATIVOS.includes(rol);

  const mutacion = useMutation({
    mutationFn: () => {
      const datos: CrearUsuario = {
        email,
        nombre,
        telefono,
        rol,
        claveInicial,
        // El rol dice qué puedes hacer; la ubicación, sobre qué. Solo los
        // operativos la llevan, y el backend lo verifica igual.
        ...(operativo ? { ubicacionAsignada: ubicacion } : {}),
      };
      return crearUsuario(datos);
    },
    onSuccess: alCrear,
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Card titulo="Nuevo usuario">
      <Aviso>
        La contraseña que pongas aquí es temporal: la persona está obligada a cambiarla la primera
        vez que entre, y hasta entonces no puede hacer nada más.
      </Aviso>

      <form
        className="mt-3 grid gap-3 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          mutacion.mutate();
        }}
      >
        <Campo etiqueta="Nombre" valor={nombre} alCambiar={setNombre} />
        <Campo etiqueta="Correo" valor={email} alCambiar={setEmail} tipo="email" />
        <Campo
          etiqueta="Teléfono"
          valor={telefono}
          alCambiar={setTelefono}
          tipo="tel"
          ayuda="A quién llamar. Se guarda tal como lo escribas."
        />

        <label className="block">
          <span className="mb-1 block text-sm font-medium">Rol</span>
          <select
            value={rol}
            onChange={(e) => setRol(e.target.value as Rol)}
            className="w-full rounded-md border border-borde bg-superficie px-3 py-2 text-sm"
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r.replaceAll('_', ' ').toLowerCase()}
              </option>
            ))}
          </select>
        </label>

        {operativo && (
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Ubicación asignada</span>
            <select
              value={ubicacion}
              onChange={(e) => setUbicacion(e.target.value as Ubicacion)}
              className="w-full rounded-md border border-borde bg-superficie px-3 py-2 text-sm"
            >
              <option value="PLANTA">planta</option>
              <option value="BODEGA_BOGOTA">bodega bogotá</option>
            </select>
          </label>
        )}

        <Campo
          etiqueta="Contraseña temporal"
          valor={claveInicial}
          alCambiar={setClaveInicial}
          ayuda="Mínimo 12 caracteres. Entrégasela en persona."
        />

        <div className="sm:col-span-2">
          {error !== null && <Error_>{error}</Error_>}
          <Boton submit deshabilitado={mutacion.isPending}>
            {mutacion.isPending ? 'Creando…' : 'Crear usuario'}
          </Boton>
        </div>
      </form>
    </Card>
  );
}

function Campo({
  etiqueta,
  valor,
  alCambiar,
  tipo = 'text',
  ayuda,
}: {
  etiqueta: string;
  valor: string;
  alCambiar: (v: string) => void;
  tipo?: string;
  ayuda?: string;
}): ReactNode {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium">{etiqueta}</span>
      <input
        type={tipo}
        value={valor}
        onChange={(e) => alCambiar(e.target.value)}
        required
        className="w-full rounded-md border border-borde bg-superficie px-3 py-2 text-sm"
      />
      {ayuda !== undefined && <span className="mt-1 block text-xs text-tenue">{ayuda}</span>}
    </label>
  );
}
